import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { Youtube, Save, Users, Plus, CheckCircle2, Circle } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import AdminBadge from "@/components/admin/AdminBadge";
import AdminButton from "@/components/admin/AdminButton";
import AdminEmptyState from "@/components/admin/AdminEmptyState";
import { detectMedia } from "@/lib/media";

// Every student's own start date/override puts them on a different week (a
// different pair of stories) at the same calendar moment, so recap links
// are keyed by cycle_number, not by a global setting — see
// GET /admin/weekly-recaps and GET /weekly-recap/current in server.py.
export default function AdminWeeklyRecaps() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(null);
  const [addWeek, setAddWeek] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/weekly-recaps");
      setWeeks(data.weeks);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateLocal = (cycle_number, patch) => {
    setWeeks((prev) => prev.map((w) => (w.cycle_number === cycle_number ? { ...w, ...patch } : w)));
  };

  const save = async (week) => {
    setErr(""); setMsg(""); setSaving(week.cycle_number);
    try {
      await client.put(`/admin/weekly-recaps/${week.cycle_number}`, {
        enabled: week.enabled,
        youtube_url: week.youtube_url,
        schedule_note: week.schedule_note,
      });
      setMsg(`Week ${week.cycle_number} saved.`);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(null);
    }
  };

  const addManualWeek = () => {
    const n = Number(addWeek);
    if (!n || n < 1) return;
    if (weeks.some((w) => w.cycle_number === n)) { setAddWeek(""); return; }
    setWeeks((prev) => [...prev, {
      cycle_number: n, story_a_seq: n * 2 - 1, story_b_seq: n * 2,
      student_count: 0, enabled: true, youtube_url: "", schedule_note: "",
    }].sort((a, b) => a.cycle_number - b.cycle_number));
    setAddWeek("");
  };

  return (
    <div data-testid="admin-weekly-recaps">
      <AdminPageHeader
        icon={Youtube}
        title="Weekly Recap Links"
        subtitle="One link per week — each student only ever sees the recap for the pair of stories they're actually on"
      />

      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="text-xs font-semibold text-hv-slate block mb-1">Add a week by number</label>
          <input type="number" min="1" value={addWeek} onChange={(e) => setAddWeek(e.target.value)}
                 placeholder="e.g. 5" className="w-28 rounded-full border border-[#E5DFC8] px-3 py-2 text-sm" />
        </div>
        <AdminButton variant="secondary" icon={Plus} onClick={addManualWeek}>Add week</AdminButton>
      </div>

      {err && <div className="mb-4 text-hv-coral font-bold text-sm" data-testid="recaps-error">{err}</div>}
      {msg && <div className="mb-4 text-hv-teal font-bold text-sm" data-testid="recaps-msg">{msg}</div>}

      {loading ? (
        <p className="text-hv-slate text-sm">Loading…</p>
      ) : weeks.length === 0 ? (
        <AdminEmptyState icon={Youtube} title="No weeks in progress yet."
                         subtitle="Once students enroll, their current week shows up here automatically." />
      ) : (
        <div className="space-y-4">
          {weeks.map((w) => (
            <div key={w.cycle_number} className="rounded-2xl bg-white border border-[#E5DFC8] p-5" data-testid={`recap-week-${w.cycle_number}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="hv-heading text-lg text-hv-navy">Week {w.cycle_number} — Stories {w.story_a_seq} &amp; {w.story_b_seq}</h3>
                  <AdminBadge tone={w.student_count > 0 ? "success" : "neutral"}>
                    <Users size={12} /> {w.student_count} student{w.student_count === 1 ? "" : "s"} here now
                  </AdminBadge>
                </div>
                <button type="button" onClick={() => updateLocal(w.cycle_number, { enabled: !w.enabled })}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-hv-navy">
                  {w.enabled ? <CheckCircle2 size={16} className="text-hv-teal" /> : <Circle size={16} className="text-hv-slate" />}
                  {w.enabled ? "Visible to students" : "Hidden"}
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-hv-slate block mb-1">Video link</label>
                  <input value={w.youtube_url || ""} placeholder="YouTube, Vimeo, or Google Drive link"
                         onChange={(e) => updateLocal(w.cycle_number, { youtube_url: e.target.value })}
                         data-testid={`recap-week-${w.cycle_number}-url`}
                         className="w-full rounded-xl border border-[#E5DFC8] focus:border-hv-teal bg-hv-cream px-3 py-2 text-sm outline-none" />
                  {w.youtube_url && (
                    <p className="text-xs text-hv-slate mt-1">Detected: <span className="font-semibold text-hv-navy">{detectMedia(w.youtube_url).type}</span></p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-hv-slate block mb-1">Schedule note</label>
                  <input value={w.schedule_note || ""} placeholder="e.g. Sundays, 5:00 PM"
                         onChange={(e) => updateLocal(w.cycle_number, { schedule_note: e.target.value })}
                         className="w-full rounded-xl border border-[#E5DFC8] focus:border-hv-teal bg-hv-cream px-3 py-2 text-sm outline-none" />
                </div>
              </div>

              <div className="mt-3 flex justify-end">
                <AdminButton variant="primary" icon={Save} disabled={saving === w.cycle_number}
                             onClick={() => save(w)} data-testid={`recap-week-${w.cycle_number}-save`}>
                  {saving === w.cycle_number ? "Saving…" : "Save"}
                </AdminButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
